import {
    Autocomplete, Box, Card, CardHeader, Checkbox, Grid, IconButton, List, ListItem,
    ListItemButton, ListItemIcon, ListItemText, Stack, TextField, Typography
} from '@mui/material'
import CachedIcon from '@mui/icons-material/Cached'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Button } from 'src/components/forms'
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import { EntityWithIdAndDescriptionFields } from 'src/types'
import { HttpSectorSistemaGrupo } from './api'
import { HttpSectorComercialABM } from '../sectores-comerciales/api'
import { SistemaDisponibleDTO, SistemaDisponibleFields, GrupoDisponibleDTO, GrupoDisponibleFields } from './types'
import { Controller, useForm } from 'react-hook-form'

type FilterForm = { codPais: any; codSectorComercial: any }

function TabConfPorSector() {
    const { paisLogos } = usePaisLogo()
    const [sectores, setSectores] = useState<{ id: number; descripcion: string }[]>([])
    const [sistemas, setSistemas] = useState<SistemaDisponibleDTO[]>([])
    const [checkedSistemas, setCheckedSistemas] = useState<number[]>([])
    const [selectedSistema, setSelectedSistema] = useState<SistemaDisponibleDTO | null>(null)
    const [grupos, setGrupos] = useState<GrupoDisponibleDTO[]>([])
    const [checkedGrupos, setCheckedGrupos] = useState<number[]>([])

    const { control, watch, setValue } = useForm<FilterForm>({ defaultValues: { codPais: null, codSectorComercial: null } })
    const selectedPais = watch('codPais')
    const selectedSector = watch('codSectorComercial')
    const codPais = selectedPais?.id ?? selectedPais
    const codSector = selectedSector?.id ?? selectedSector

    useEffect(() => {
        if (!selectedPais) { setSectores([]); setValue('codSectorComercial', null); return }
        HttpSectorComercialABM.getSectoresComerciales().then(data => {
            const filtered = data
                .filter(s => s.codPais === codPais)
                .map(s => ({ id: s.id, descripcion: `${s.nombreUnidadNegocio ?? ''} - ${s.nombreSectorComercial}` }))
                .sort((a, b) => a.descripcion.localeCompare(b.descripcion))
            setSectores(filtered)
            setValue('codSectorComercial', null)
        })
    }, [selectedPais])

    useEffect(() => {
        if (!codSector) { setSistemas([]); setSelectedSistema(null); setGrupos([]); return }
        fetchSistemas()
    }, [selectedSector])

    const fetchSistemas = () => {
        if (!codSector) return
        HttpSectorSistemaGrupo.getSistemas(codSector, codPais).then(data => {
            setSistemas(data)
            setCheckedSistemas(data.filter(s => s[SistemaDisponibleFields.Activo]).map(s => s[SistemaDisponibleFields.CodSistema]))
            setSelectedSistema(null); setGrupos([]); setCheckedGrupos([])
        })
    }

    useEffect(() => {
        if (!selectedSistema || !codSector) { setGrupos([]); setCheckedGrupos([]); return }
        HttpSectorSistemaGrupo.getGruposPorSistema(codSector, selectedSistema[SistemaDisponibleFields.CodSistema])
            .then(data => {
                setGrupos(data)
                setCheckedGrupos(data.filter(g => g[GrupoDisponibleFields.PerteneceASector] === 1).map(g => g[GrupoDisponibleFields.IdGrupo]))
            })
    }, [selectedSistema])

    const handleToggleGrupo = (idGrupo: number) => {
        setCheckedGrupos(prev => prev.includes(idGrupo) ? prev.filter(id => id !== idGrupo) : [...prev, idGrupo])
    }
    const handleSaveGrupos = () => {
        if (!codSector) return Promise.resolve()
        const promise = HttpSectorSistemaGrupo.updateGrupos(codSector, checkedGrupos).then(() => {
            // Refrescar sistemas (se actualizan por trigger)
            fetchSistemas()
            if (selectedSistema) {
                HttpSectorSistemaGrupo.getGruposPorSistema(codSector, selectedSistema[SistemaDisponibleFields.CodSistema]).then(data => {
                    setGrupos(data)
                    setCheckedGrupos(data.filter(g => g[GrupoDisponibleFields.PerteneceASector] === 1).map(g => g[GrupoDisponibleFields.IdGrupo]))
                })
            }
        })
        toast.promise(promise, { success: 'Grupos actualizados', error: 'Error al guardar', loading: 'Guardando' })
        return promise
    }

    return (
        <Card>
            <CardHeader title='Configuración por Sector' subheader='País → Sector Comercial → Sistemas → Grupos'
                action={codSector ? <IconButton onClick={fetchSistemas}><CachedIcon /></IconButton> : null}
            />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mx: 2, mb: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                <Box sx={{ minWidth: 200, flex: 1 }}>
                    <Controller control={control} name='codPais' render={({ field }) => (
                        <Autocomplete options={paisLogos.map(s => ({ id: s.id, descripcion: s[EntityWithIdAndDescriptionFields.Description] }))}
                            value={field.value} onChange={(_, v) => field.onChange(v)}
                            getOptionLabel={(o: any) => o.descripcion ?? ''} isOptionEqualToValue={(o: any, v: any) => o.id === v?.id}
                            size='small' renderInput={params => <TextField {...params} label='País *' />}
                        />
                    )} />
                </Box>
                <Box sx={{ minWidth: 300, flex: 2 }}>
                    <Controller control={control} name='codSectorComercial' render={({ field }) => (
                        <Autocomplete disabled={!selectedPais} options={sectores}
                            value={field.value} onChange={(_, v) => field.onChange(v)}
                            getOptionLabel={(o: any) => o.descripcion ?? ''} isOptionEqualToValue={(o: any, v: any) => o.id === v?.id}
                            size='small' renderInput={params => <TextField {...params} label='Sector Comercial *' />}
                        />
                    )} />
                </Box>
            </Box>

            {codSector ? (
                <Box sx={{ mx: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={5}>
                            <Card variant='outlined' sx={{ height: 500, display: 'flex', flexDirection: 'column' }}>
                                <CardHeader title='Sistemas' titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} sx={{ pb: 0 }} />
                                <Box sx={{ overflow: 'auto', flex: 1 }}>
                                    <List dense>
                                        {sistemas.map(sistema => {
                                            const cod = sistema[SistemaDisponibleFields.CodSistema]
                                            return (
                                                <ListItem key={cod} disablePadding>
                                                    <ListItemButton selected={selectedSistema?.[SistemaDisponibleFields.CodSistema] === cod} onClick={() => setSelectedSistema(sistema)}>
                                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                                            <Checkbox edge='start' checked={checkedSistemas.includes(cod)} size='small' sx={{ pointerEvents: 'none' }} />
                                                        </ListItemIcon>
                                                        <ListItemText primary={sistema[SistemaDisponibleFields.NombreSistema]} />
                                                    </ListItemButton>
                                                </ListItem>
                                            )
                                        })}
                                        {sistemas.length === 0 && <ListItem><ListItemText primary='No hay sistemas disponibles' sx={{ color: 'text.secondary', textAlign: 'center' }} /></ListItem>}
                                    </List>
                                </Box>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Card variant='outlined' sx={{ height: 500, display: 'flex', flexDirection: 'column' }}>
                                <CardHeader title={selectedSistema ? `Grupos de ${selectedSistema[SistemaDisponibleFields.NombreSistema]}` : 'Grupos'}
                                    titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} sx={{ pb: 0 }}
                                    action={selectedSistema && grupos.length > 0 ? <Button variant='contained' color='primary' size='small' promise={handleSaveGrupos}>Guardar</Button> : null}
                                />
                                <Box sx={{ overflow: 'auto', flex: 1 }}>
                                    {selectedSistema ? (
                                        <List dense>
                                            {grupos.map(grupo => (
                                                <ListItem key={grupo[GrupoDisponibleFields.IdGrupo]} disablePadding>
                                                    <ListItemButton onClick={() => handleToggleGrupo(grupo[GrupoDisponibleFields.IdGrupo])}>
                                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                                            <Checkbox edge='start' checked={checkedGrupos.includes(grupo[GrupoDisponibleFields.IdGrupo])} tabIndex={-1} disableRipple size='small' />
                                                        </ListItemIcon>
                                                        <ListItemText primary={grupo[GrupoDisponibleFields.NombreGrupo]} />
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                            {grupos.length === 0 && <ListItem><ListItemText primary='No hay grupos para este sistema' sx={{ color: 'text.secondary', textAlign: 'center' }} /></ListItem>}
                                        </List>
                                    ) : (
                                        <Box sx={{ p: 4, textAlign: 'center' }}><Typography color='text.secondary'>Seleccione un sistema para ver sus grupos</Typography></Box>
                                    )}
                                </Box>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}><Typography color='text.secondary'>Seleccione un país y sector comercial para configurar</Typography></Box>
            )}
        </Card>
    )
}

export default TabConfPorSector
